import { installEqualWebLaunchButtonOffscreenStyle } from './accessibility';

const VLIBRAS_PLUGIN_SRC = 'https://vlibras.gov.br/app/vlibras-plugin.js';
const VLIBRAS_APP_BASE = 'https://vlibras.gov.br/app';

const WIDGET_READY_TIMEOUT_MS = 15000;
const ACCESS_BUTTON_TIMEOUT_MS = 8000;
const MENU_OBSERVER_TIMEOUT_MS = 120000;
const EQUALWEB_MENU_SELECTORS = ['#INDmenuBtnzWrap', '#INDmenu-list', '#INDmenu-options'];
const EQUALWEB_SHADOW_HOST_SELECTORS = ['#INDShadowRootHost', '#INDshadowRootWrap'];

const VLIBRAS_BUTTON_ICON = `<svg version="1.2" baseProfile="tiny" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="126.04px" height="122.666px" viewBox="0 0 126.04 122.666" xml:space="preserve"><path d="M108.875,61.237c0.002-10.126-4.098-19.334-10.711-25.996c-1.361-1.374-3.58-1.383-4.951-0.021c-1.372,1.361-1.381,3.577-0.02,4.951v-0.002c5.371,5.421,8.681,12.847,8.681,21.068c0.003,0.016,0,0.074,0.003,0.17c-0.032,8.219-3.401,15.663-8.842,21.071c-1.372,1.361-1.379,3.577-0.018,4.949c0.686,0.688,1.585,1.034,2.484,1.034c0.893,0,1.784-0.339,2.467-1.018c6.695-6.646,10.873-15.881,10.906-26.063V61.237z M109.015,19.872c-1.364-1.372-3.579-1.381-4.952-0.019c-1.369,1.363-1.378,3.579-0.016,4.951v-0.002c9.273,9.353,14.992,22.19,14.992,36.389c0,0.049,0,0.134,0.002,0.253c-0.058,14.206-5.878,27.071-15.267,36.398c-1.372,1.362-1.381,3.58-0.017,4.952c0.684,0.689,1.584,1.034,2.484,1.034c0.891,0,1.781-0.338,2.465-1.016c10.648-10.569,17.273-25.227,17.332-41.405v-0.217C126.042,45.092,119.536,30.468,109.015,19.872z M81.307,0.362c-1.189-0.59-2.621-0.451-3.677,0.355L35.889,32.576H3.502c-0.924,0-1.824,0.372-2.476,1.025C0.375,34.253,0,35.153,0,36.075v50.516c0,0.922,0.375,1.822,1.026,2.476c0.651,0.651,1.554,1.024,2.476,1.024H35.89l41.74,31.858c0.622,0.474,1.372,0.717,2.128,0.717c0.527,0,1.059-0.119,1.549-0.361c1.189-0.59,1.947-1.81,1.947-3.136V3.5C83.254,2.17,82.497,0.949,81.307,0.362z M76.255,112.092L39.196,83.809c-0.606-0.464-1.36-0.718-2.122-0.718H7V39.575h30.074c0.762,0,1.516-0.255,2.122-0.717l37.059-28.286V112.092z"></path></svg> <span id="vlibras_label_1" aria-hidden="true" class="INDmenuBtn-text">V Libras</span> <span id="vlibras_label_2" aria-hidden="true" class="INDmenuBtn-desc">Aciona o V Libras</span>`;

type WidgetCtor = new (appBaseUrl: string) => unknown;

let widgetReadyPromise: Promise<void> | null = null;
let menuObserverStarted = false;

function ensureVlibrasMount(): void {
  if (document.getElementById('vlibras')) {
    return;
  }

  const mount = document.createElement('div');
  mount.id = 'vlibras';
  mount.setAttribute('vw', '');
  mount.className = 'enabled';
  mount.innerHTML = `
    <div vw-access-button class="active" id="vlibrasclick"></div>
    <div vw-plugin-wrapper>
      <div class="vw-plugin-top-wrapper"></div>
    </div>
  `;

  document.body.appendChild(mount);
}

function waitUntil<T>(
  predicate: () => T | null | undefined,
  options: { timeoutMs: number; intervalMs: number },
): Promise<T | null> {
  return new Promise(resolve => {
    const start = Date.now();
    const tick = () => {
      try {
        const value = predicate();
        if (value) {
          resolve(value);
          return;
        }
      } catch {} // eslint-disable-line no-empty

      if (Date.now() - start >= options.timeoutMs) {
        resolve(null);
        return;
      }

      window.setTimeout(tick, options.intervalMs);
    };
    tick();
  });
}

function loadPluginScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${VLIBRAS_PLUGIN_SRC}"]`,
    );

    if (existing) {
      if (existing.dataset.vlibrasLoaded === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Falha ao carregar vlibras-plugin.js')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = VLIBRAS_PLUGIN_SRC;
    script.async = true;
    script.addEventListener(
      'load',
      () => {
        script.dataset.vlibrasLoaded = 'true';
        resolve();
      },
      { once: true },
    );
    script.addEventListener(
      'error',
      () => reject(new Error('Falha ao carregar vlibras-plugin.js')),
      { once: true },
    );
    document.body.appendChild(script);
  });
}

function resolveWidgetCtor(): WidgetCtor | null {
  const raw = ((window as unknown) as { VLibras?: any }).VLibras;
  if (!raw) return null;
  if (typeof raw.Widget === 'function') return raw.Widget as WidgetCtor;
  if (typeof raw.default?.Widget === 'function') return raw.default.Widget as WidgetCtor;
  return null;
}

function queryInRoots(roots: Array<Document | ShadowRoot>, selectors: string[]): Element | null {
  return roots.reduce<Element | null>((foundInRoot, root) => {
    if (foundInRoot) return foundInRoot;
    return selectors.reduce<Element | null>(
      (found, selector) => found ?? root.querySelector(selector),
      null,
    );
  }, null);
}

function collectEqualWebShadowRoots(): ShadowRoot[] {
  return EQUALWEB_SHADOW_HOST_SELECTORS.reduce<ShadowRoot[]>((acc, selector) => {
    const host = document.querySelector(selector) as Element | null;
    if (host && (host as HTMLElement & { shadowRoot?: ShadowRoot }).shadowRoot) {
      acc.push((host as HTMLElement & { shadowRoot: ShadowRoot }).shadowRoot);
    }
    return acc;
  }, []);
}

function findEqualWebOptionsContainer(roots: Array<Document | ShadowRoot>): Element | null {
  return roots.reduce<Element | null>((found, root) => {
    if (found) return found;
    const samples = root.querySelectorAll<HTMLElement>('[data-indopt]');
    if (samples.length === 0) return null;

    const candidates = new Map<Element, number>();
    samples.forEach(node => {
      let parent: Element | null = node.parentElement;
      while (parent) {
        candidates.set(parent, (candidates.get(parent) ?? 0) + 1);
        parent = parent.parentElement;
      }
    });

    let best: Element | null = null;
    let bestDepth = -1;
    candidates.forEach((count, candidate) => {
      if (count !== samples.length) return;
      let depth = 0;
      let cursor: Element | null = candidate.parentElement;
      while (cursor) {
        depth += 1;
        cursor = cursor.parentElement;
      }
      if (depth > bestDepth) {
        bestDepth = depth;
        best = candidate;
      }
    });

    return best;
  }, null);
}

function findEqualWebMenu(): Element | null {
  const roots: Array<Document | ShadowRoot> = [document, ...collectEqualWebShadowRoots()];
  return queryInRoots(roots, EQUALWEB_MENU_SELECTORS) ?? findEqualWebOptionsContainer(roots);
}

function findVlibrasAccessButton(): HTMLElement | null {
  return (
    document.querySelector<HTMLElement>('#vlibras [vw-access-button]') ??
    document.querySelector<HTMLElement>('[vw-access-button]')
  );
}

const VLIBRAS_OVERRIDE_STYLE_ID = 'vlibras-override-style';

function ensureVlibrasOverrideStyles(): void {
  if (document.getElementById(VLIBRAS_OVERRIDE_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = VLIBRAS_OVERRIDE_STYLE_ID;
  style.textContent = `
    div[vw] {
      z-index: 2147483647 !important;
    }

    div[vw] [vw-access-button],
    div[vw] [vw-plugin-wrapper] {
      z-index: 2147483647 !important;
    }

    #vlibras [vw-access-button] {
      position: fixed !important;
      left: -10000px !important;
      top: 0 !important;
      width: 1px !important;
      height: 1px !important;
      overflow: hidden !important;
      opacity: 0 !important;
      clip: rect(0, 0, 0, 0) !important;
      border: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
    }
  `;
  document.head.appendChild(style);
}

function ensureVlibrasRootVisible(mount: HTMLElement): void {
  const { style } = mount;
  style.display = 'block';
  ensureVlibrasOverrideStyles();
}

async function openVlibrasPopup(): Promise<void> {
  const root = document.getElementById('vlibras');
  if (root) ensureVlibrasRootVisible(root);

  const accessButton = await waitUntil(findVlibrasAccessButton, {
    timeoutMs: ACCESS_BUTTON_TIMEOUT_MS,
    intervalMs: 100,
  });

  const trigger = accessButton ?? document.getElementById('vlibrasclick');
  if (!trigger) {
    return;
  }
  trigger.click();

  if (accessButton && typeof accessButton.focus === 'function') {
    accessButton.setAttribute('tabindex', accessButton.getAttribute('tabindex') ?? '0');
    accessButton.focus({ preventScroll: true });
  }
}

let equalWebFabOnlyScheduled = false;

function ensureEqualWebFabOnly(): void {
  installEqualWebLaunchButtonOffscreenStyle();
  if (equalWebFabOnlyScheduled) return;
  equalWebFabOnlyScheduled = true;
  [0, 500, 1500, 3500, 8000].forEach(ms => {
    window.setTimeout(installEqualWebLaunchButtonOffscreenStyle, ms);
  });
}

let onEqualWebVlibrasMenuItemClick: (button: HTMLButtonElement) => Promise<void>;

function buildMenuButton(): HTMLButtonElement {
  const button = document.createElement('button');
  button.setAttribute('id', 'btvlibras');
  button.setAttribute('data-indopt', 'vlibrasreader');
  button.setAttribute('role', 'checkbox');
  button.setAttribute('aria-labelledby', 'vlibras_label_1 vlibras_label_2');
  button.setAttribute('tabindex', '0');
  button.setAttribute('aria-checked', 'false');
  button.innerHTML = VLIBRAS_BUTTON_ICON;
  button.querySelectorAll<HTMLElement>('*').forEach(el => {
    // eslint-disable-next-line no-param-reassign
    el.style.pointerEvents = 'none';
  });

  button.addEventListener('click', () => {
    onEqualWebVlibrasMenuItemClick(button).catch(() => undefined);
  });

  return button;
}

function tryInjectMenuButton(): boolean {
  const menu = findEqualWebMenu();
  if (!menu) return false;
  if (menu.querySelector('#btvlibras')) return true;
  menu.append(buildMenuButton());
  return true;
}

function observeEqualWebMenu(): void {
  if (menuObserverStarted) return;
  menuObserverStarted = true;

  tryInjectMenuButton();

  if (typeof MutationObserver === 'undefined') return;

  const observedShadowRoots = new WeakSet<ShadowRoot>();

  const hookShadowRoots = (observer: MutationObserver): void => {
    collectEqualWebShadowRoots().forEach(shadowRoot => {
      if (observedShadowRoots.has(shadowRoot)) return;
      observedShadowRoots.add(shadowRoot);
      installEqualWebLaunchButtonOffscreenStyle();
      observer.observe(shadowRoot, { childList: true, subtree: true });
    });
  };

  const observer = new MutationObserver(() => {
    hookShadowRoots(observer);
    tryInjectMenuButton();
  });

  observer.observe(document.body, { childList: true, subtree: true });
  hookShadowRoots(observer);

  window.setTimeout(() => {
    observer.disconnect();
    menuObserverStarted = false;
  }, MENU_OBSERVER_TIMEOUT_MS);
}

export function initVlibras(): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  ensureVlibrasOverrideStyles();
  ensureEqualWebFabOnly();

  if (widgetReadyPromise) {
    observeEqualWebMenu();
    return widgetReadyPromise;
  }

  widgetReadyPromise = (async () => {
    ensureVlibrasMount();
    await loadPluginScript();

    const Ctor = await waitUntil(resolveWidgetCtor, {
      timeoutMs: WIDGET_READY_TIMEOUT_MS,
      intervalMs: 100,
    });
    if (!Ctor) {
      throw new Error('window.VLibras.Widget não ficou disponível a tempo.');
    }

    const previousOnload = window.onload;
    new Ctor(VLIBRAS_APP_BASE); // eslint-disable-line no-new

    const widgetOnload = window.onload;
    const pageAlreadyLoaded = document.readyState === 'complete';

    if (
      pageAlreadyLoaded &&
      typeof widgetOnload === 'function' &&
      widgetOnload !== previousOnload
    ) {
      Promise.resolve()
        .then(() => (widgetOnload as EventListener).call(window, new Event('load')))
        .catch(() => undefined);
    }
  })();

  widgetReadyPromise.catch(() => {
    widgetReadyPromise = null;
  });

  observeEqualWebMenu();
  ensureEqualWebFabOnly();

  return widgetReadyPromise;
}

onEqualWebVlibrasMenuItemClick = async (button: HTMLButtonElement) => {
  button.blur();

  try {
    await initVlibras();
  } catch {
    return;
  }

  await openVlibrasPopup();
};
